import '../../styles/restScreens.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../config/supabase';
import { useSupportSession } from '../../context/SupportSessionContext';
import SupportEscalationModal from './SupportEscalationModal';
import SupportAuditLogView from './SupportAuditLogView';

const STATUS_STYLES = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
    active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border-green-200 dark:border-green-800',
    redeemed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    revoked: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800',
    expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
};

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
}

function shortId(value) {
    return value ? `${value.slice(0, 8)}…` : '—';
}

function SupportConsoleView({ user, theme = 'light' }) {
    const dark = theme === 'dark';
    const navigate = useNavigate();
    const {
        activeGrant,
        isSupportMode,
        enterSupportSession,
        exitSupportSession,
        annotations,
        escalateSupportSession,
        sessionNotice,
        clearSessionNotice
    } = useSupportSession();

    const [grants, setGrants] = useState([]);
    const [orgsMap, setOrgsMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [filterTab, setFilterTab] = useState('all'); // all, active, pending, history
    const [showEscalationModal, setShowEscalationModal] = useState(false);

    // Redemption modal state
    const [redeemModalGrant, setRedeemModalGrant] = useState(null);
    const [accessCodeInput, setAccessCodeInput] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [redeemError, setRedeemError] = useState(null);

    const loadGrants = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Load grants where support_user_id = user.id or all grants accessible to super admin
            const { data: grantsData, error: grantsError } = await supabaseClient
                .from('support_access_grants')
                .select('*')
                .order('created_at', { ascending: false });

            if (grantsError) {
                throw grantsError;
            }

            const currentGrants = grantsData || [];
            setGrants(currentGrants);

            // Fetch corresponding org names
            const orgIds = Array.from(new Set(currentGrants.map((g) => g.org_id).filter(Boolean)));
            if (orgIds.length > 0) {
                const { data: orgsData } = await supabaseClient
                    .from('orgs')
                    .select('id, name')
                    .in('id', orgIds);

                if (orgsData) {
                    setOrgsMap(new Map(orgsData.map((o) => [o.id, o.name])));
                }
            }
        } catch (err) {
            console.error('Error loading support grants:', err);
            setError(err.message || 'Failed to load support grants');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadGrants();
    }, [loadGrants]);

    const handleRedeemCode = async (e) => {
        e.preventDefault();
        if (!redeemModalGrant || !accessCodeInput.trim()) return;

        setRedeeming(true);
        setRedeemError(null);

        try {
            const res = await supabaseClient.rpc('redeem_support_access_code', {
                p_grant_id: redeemModalGrant.id,
                p_access_code: accessCodeInput.trim()
            });

            if (res && res.error) {
                throw res.error;
            }

            const orgName = orgsMap.get(redeemModalGrant.org_id) || redeemModalGrant.org_id;
            setNotice(`Access code redeemed successfully! Support window is now open.`);

            // Update grant state
            const updatedGrant = {
                ...redeemModalGrant,
                status: 'redeemed',
                redeemed_at: new Date().toISOString()
            };

            setRedeemModalGrant(null);
            setAccessCodeInput('');
            await loadGrants();

            // Automatically prompt or enter support session
            if (window.confirm(`Support access code redeemed! Would you like to enter the live support session for ${orgName} now?`)) {
                enterSupportSession(updatedGrant, orgName);
                navigate('/dashboard');
            }
        } catch (err) {
            console.error('Error redeeming code:', err);
            setRedeemError(err.message || 'Failed to redeem support access code.');
        } finally {
            setRedeeming(false);
        }
    };

    const handleEnterSession = (grant) => {
        const orgName = orgsMap.get(grant.org_id) || grant.org_id;
        enterSupportSession(grant, orgName);
        navigate('/dashboard');
    };

    const handleEscalateSession = async (findings) => {
        const result = await escalateSupportSession(findings);
        if (!result?.error) {
            await loadGrants();
        }
        return result;
    };

    const isEnterable = (grant) => {
        const expiresAt = new Date(grant.access_expires_at);
        const isValidExpiry = !Number.isNaN(expiresAt.getTime()) && expiresAt > new Date();
        const isRedeemed = grant.status === 'redeemed' || (grant.status === 'active' && Boolean(grant.redeemed_at));
        return isValidExpiry && isRedeemed && !grant.revoked_at;
    };

    const isRedeemable = (grant) => {
        const expiresAt = new Date(grant.access_expires_at);
        const codeExpiresAt = grant.code_expires_at ? new Date(grant.code_expires_at) : null;
        const isValidAccess = !Number.isNaN(expiresAt.getTime()) && expiresAt > new Date();
        const isValidCode = codeExpiresAt && codeExpiresAt > new Date();
        return grant.status === 'active' && !grant.redeemed_at && !grant.revoked_at && isValidAccess && isValidCode;
    };

    const filteredGrants = useMemo(() => {
        const now = new Date();
        return grants.filter((grant) => {
            const accessExpired = new Date(grant.access_expires_at) <= now;
            if (filterTab === 'active') {
                return !accessExpired && !grant.revoked_at && ['active', 'redeemed'].includes(grant.status);
            }
            if (filterTab === 'pending') {
                return grant.status === 'pending' || (grant.status === 'active' && !grant.redeemed_at && !accessExpired);
            }
            if (filterTab === 'history') {
                return accessExpired || grant.status === 'revoked' || grant.status === 'expired';
            }
            return true;
        });
    }, [grants, filterTab]);

    const stats = useMemo(() => {
        const now = new Date();
        let activeCount = 0;
        let pendingCount = 0;
        let historyCount = 0;

        grants.forEach((g) => {
            const accessExpired = new Date(g.access_expires_at) <= now;
            if (!accessExpired && !g.revoked_at && ['active', 'redeemed'].includes(g.status)) {
                activeCount++;
            } else if (g.status === 'pending' || (g.status === 'active' && !g.redeemed_at && !accessExpired)) {
                pendingCount++;
            } else {
                historyCount++;
            }
        });

        return { total: grants.length, activeCount, pendingCount, historyCount };
    }, [grants]);

    const cardClass = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const textClass = dark ? 'text-gray-100' : 'text-gray-900';
    const mutedClass = dark ? 'text-gray-400' : 'text-gray-600';

    return (
        <div data-m6-layout="support" data-m6-component="SupportConsoleView" className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h2 className={`text-2xl font-bold flex items-center gap-2.5 ${textClass}`}>
                        <span>🛠️</span>
                        <span>Support Console</span>
                        <span className="text-xs px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300 border border-orange-200 dark:border-orange-800 font-semibold uppercase tracking-wider">
                            Platform Staff
                        </span>
                    </h2>
                    <p className={`mt-1 text-sm ${mutedClass}`}>
                        Manage time-boxed tenant support access grants and launch live read-only troubleshooting sessions.
                    </p>
                </div>
                <button
                    onClick={loadGrants}
                    disabled={loading}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition ${
                        dark
                            ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    {loading ? 'Refreshing…' : '↻ Refresh'}
                </button>
            </div>

            {/* Active Session Highlight Banner */}
            {isSupportMode && activeGrant && (
                <div className="rounded-xl border border-amber-500 bg-amber-50 dark:bg-amber-950/40 p-4 shadow-sm flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">⚡</span>
                        <div>
                            <div className="font-bold text-amber-900 dark:text-amber-200 text-sm">
                                Live Support Session Active: {activeGrant.org_name || activeGrant.org_id}
                            </div>
                            <div className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                                Reason: &ldquo;{activeGrant.reason}&rdquo; — Read-only tenant mode enabled.
                            </div>
                            {activeGrant.escalated_issue_url && (
                                <a
                                    href={activeGrant.escalated_issue_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-1 inline-flex text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline"
                                >
                                    Engineering escalation created
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!activeGrant.escalated_issue_url && (
                            <button
                                type="button"
                                onClick={() => setShowEscalationModal(true)}
                                className="px-3 py-1.5 rounded-lg bg-red-700 hover:bg-red-800 text-white text-xs font-semibold shadow"
                            >
                                Escalate to Engineering
                            </button>
                        )}
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold shadow"
                        >
                            Return to App View
                        </button>
                        <button
                            onClick={() => exitSupportSession('Support session closed by operator.', { revoke: true })}
                            className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold shadow"
                        >
                            Exit Session
                        </button>
                    </div>
                </div>
            )}

            {/* Notices */}
            {sessionNotice && (
                <div className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-950/50 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 flex justify-between items-center">
                    <span>{sessionNotice}</span>
                    <button onClick={clearSessionNotice} className="text-amber-700 dark:text-amber-300 hover:underline text-xs">Dismiss</button>
                </div>
            )}
            {error && <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-800 dark:text-red-300">{error}</div>}
            {notice && <div className="rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/40 px-4 py-3 text-sm text-green-800 dark:text-green-300">{notice}</div>}

            {/* Metrics Row */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className={`p-4 rounded-xl border ${cardClass}`}>
                    <div className={`text-xs font-medium uppercase tracking-wider ${mutedClass}`}>Total Grants</div>
                    <div className={`text-2xl font-bold mt-1 ${textClass}`}>{stats.total}</div>
                </div>
                <div className={`p-4 rounded-xl border ${cardClass}`}>
                    <div className="text-xs font-medium uppercase tracking-wider text-green-600 dark:text-green-400">Active / Enterable</div>
                    <div className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400">{stats.activeCount}</div>
                </div>
                <div className={`p-4 rounded-xl border ${cardClass}`}>
                    <div className="text-xs font-medium uppercase tracking-wider text-yellow-600 dark:text-yellow-400">Pending Codes</div>
                    <div className="text-2xl font-bold mt-1 text-yellow-600 dark:text-yellow-400">{stats.pendingCount}</div>
                </div>
                <div className={`p-4 rounded-xl border ${cardClass}`}>
                    <div className={`text-xs font-medium uppercase tracking-wider ${mutedClass}`}>Expired / Revoked</div>
                    <div className={`text-2xl font-bold mt-1 ${mutedClass}`}>{stats.historyCount}</div>
                </div>
            </div>

            {/* Grants List & Tabs */}
            <div className={`rounded-xl border overflow-hidden ${cardClass}`}>
                <div className={`px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                        <span className="text-lg">📋</span>
                        <h3 className={`font-semibold ${textClass}`}>Support Access Grants</h3>
                    </div>

                    {/* Filter Tabs */}
                    <div className={`inline-flex rounded-lg border p-0.5 text-xs font-medium ${dark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-100'}`}>
                        {[
                            { key: 'all', label: `All (${stats.total})` },
                            { key: 'active', label: `Active (${stats.activeCount})` },
                            { key: 'pending', label: `Pending (${stats.pendingCount})` },
                            { key: 'history', label: `History (${stats.historyCount})` },
                            { key: 'audit_log', label: 'Audit Log' }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setFilterTab(tab.key)}
                                className={`px-3 py-1.5 rounded-md transition ${
                                    filterTab === tab.key
                                        ? dark
                                            ? 'bg-gray-800 text-white shadow-sm'
                                            : 'bg-white text-gray-900 shadow-sm'
                                        : dark
                                            ? 'text-gray-400 hover:text-gray-200'
                                            : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {filterTab === 'audit_log' ? (
                    <div className="p-5">
                        <SupportAuditLogView dark={dark} />
                    </div>
                ) : loading ? (
                    <div className={`p-8 text-center text-sm ${mutedClass}`}>Loading support access grants…</div>
                ) : filteredGrants.length === 0 ? (
                    <div className={`p-8 text-center text-sm ${mutedClass}`}>
                        No support access grants match the selected filter.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={dark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Status</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Organization</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Reason</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Access Window</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Escalation</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Created</th>
                                    <th className={`px-5 py-3 text-right text-xs font-semibold uppercase ${mutedClass}`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${dark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {filteredGrants.map((grant) => {
                                    const orgName = orgsMap.get(grant.org_id) || shortId(grant.org_id);
                                    const isCurrentSession = activeGrant?.id === grant.id;
                                    const canEnter = isEnterable(grant);
                                    const canRedeem = isRedeemable(grant);

                                    return (
                                        <tr key={grant.id} className={dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}>
                                            <td className="px-5 py-4">
                                                <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold border ${STATUS_STYLES[grant.status] || STATUS_STYLES.expired}`}>
                                                    {grant.status}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className={`text-sm font-semibold ${textClass}`}>{orgName}</div>
                                                <div className="font-mono text-xs text-gray-500">{shortId(grant.org_id)}</div>
                                            </td>
                                            <td className={`px-5 py-4 text-sm max-w-xs ${textClass}`}>
                                                <div className="truncate" title={grant.reason}>{grant.reason}</div>
                                            </td>
                                            <td className="px-5 py-4 text-xs">
                                                <div className={textClass}>Expires: {formatDate(grant.access_expires_at)}</div>
                                                {grant.code_expires_at && !grant.redeemed_at && (
                                                    <div className="text-yellow-600 dark:text-yellow-400 mt-0.5">
                                                        Code expires: {formatDate(grant.code_expires_at)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 text-xs">
                                               {grant.escalated_issue_url ? (
                                                   <a
                                                       href={grant.escalated_issue_url}
                                                       target="_blank"
                                                       rel="noopener noreferrer"
                                                       className="font-semibold text-blue-600 dark:text-blue-300 hover:underline"
                                                   >
                                                       Issue created
                                                   </a>
                                               ) : (
                                                   <span className={mutedClass}>—</span>
                                               )}
                                               {grant.escalated_at && (
                                                   <div className={mutedClass}>{formatDate(grant.escalated_at)}</div>
                                               )}
                                            </td>
                                            <td className={`px-5 py-4 text-xs ${mutedClass}`}>
                                               {formatDate(grant.created_at)}
                                            </td>
                                            <td className="px-5 py-4 text-right space-x-2">
                                                {isCurrentSession ? (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 border border-green-300">
                                                        <span>●</span> In Session
                                                    </span>
                                                ) : canEnter ? (
                                                    <button
                                                        onClick={() => handleEnterSession(grant)}
                                                        className="px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold shadow transition"
                                                    >
                                                        Enter Session
                                                    </button>
                                                ) : canRedeem ? (
                                                    <button
                                                        onClick={() => {
                                                            setRedeemModalGrant(grant);
                                                            setAccessCodeInput('');
                                                            setRedeemError(null);
                                                        }}
                                                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow transition"
                                                    >
                                                        Redeem Code
                                                    </button>
                                                ) : (
                                                    <span className={`text-xs ${mutedClass}`}>Unavailable</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Redeem Access Code Modal */}
            {redeemModalGrant && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className={`max-w-md w-full rounded-2xl border shadow-2xl p-6 ${cardClass}`}>
                        <div className="flex items-center justify-between pb-3 border-b border-gray-700">
                            <h3 className={`font-bold text-lg flex items-center gap-2 ${textClass}`}>
                                <span>🔑</span>
                                <span>Redeem Support Access Code</span>
                            </h3>
                            <button
                                onClick={() => setRedeemModalGrant(null)}
                                className="text-gray-400 hover:text-white font-bold"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleRedeemCode} className="mt-4 space-y-4">
                            <p className={`text-sm ${mutedClass}`}>
                                Enter the 6-digit one-time access code emailed to the tenant administrator for{' '}
                                <strong className={textClass}>
                                    {orgsMap.get(redeemModalGrant.org_id) || redeemModalGrant.org_id}
                                </strong>.
                            </p>

                            {redeemError && (
                                <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 p-3 text-xs text-red-700 dark:text-red-300">
                                    {redeemError}
                                </div>
                            )}

                            <div>
                                <label className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${textClass}`}>
                                    6-Digit Access Code
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    value={accessCodeInput}
                                    onChange={(e) => setAccessCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="123456"
                                    maxLength={6}
                                    required
                                    autoFocus
                                    className="w-full text-center tracking-widest text-2xl font-mono px-4 py-2.5 rounded-lg border border-gray-600 bg-gray-900 text-white outline-none focus:border-orange-500"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setRedeemModalGrant(null)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                        dark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={redeeming || accessCodeInput.length < 6}
                                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 shadow"
                                >
                                    {redeeming ? 'Redeeming…' : 'Verify & Redeem'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {showEscalationModal && (
                <SupportEscalationModal
                    activeGrant={activeGrant}
                    annotations={annotations}
                    dark={dark}
                    onClose={() => setShowEscalationModal(false)}
                    onSubmit={handleEscalateSession}
                />
            )}
        </div>
    );
}

export default SupportConsoleView;
